// Classify a dropped file or typed text into the right backend endpoint.
// Pure functions — no React state. Used by IntakeZone.

export type IntakeRoute =
  | { kind: 'pdf';    file: File }
  | { kind: 'csv';    file: File }
  | { kind: 'image';  file: File }
  | { kind: 'url';    urls: string[] }
  | { kind: 'text';   text: string }
  | { kind: 'empty' };

const URL_LINE = /^https?:\/\/\S+$/i;

export function detectFileRoute(file: File): IntakeRoute {
  const type = file.type;
  const name = file.name.toLowerCase();
  if (type === 'application/pdf' || name.endsWith('.pdf')) return { kind: 'pdf', file };
  if (type === 'text/csv' || type === 'application/vnd.ms-excel' || name.endsWith('.csv')) {
    return { kind: 'csv', file };
  }
  if (type.startsWith('image/')) return { kind: 'image', file };
  // Unknown MIME — fall back to PDF for .pdf-ish, else free-text by name
  return { kind: 'text', text: `File: ${file.name}` };
}

export function detectTextRoute(raw: string): IntakeRoute {
  const text = (raw || '').trim();
  if (!text) return { kind: 'empty' };

  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const allUrls = lines.length > 0 && lines.every((l) => URL_LINE.test(l));
  if (allUrls) return { kind: 'url', urls: lines };

  // Single URL pasted with surrounding whitespace: treat as URL
  if (lines.length === 1 && URL_LINE.test(lines[0])) return { kind: 'url', urls: lines };

  return { kind: 'text', text };
}

export function describeRoute(route: IntakeRoute): string {
  switch (route.kind) {
    case 'pdf':   return `PDF · ${route.file.name}`;
    case 'csv':   return `CSV · ${route.file.name}`;
    case 'image': return `Image · ${route.file.name}`;
    case 'url':   return route.urls.length > 1 ? `${route.urls.length} URLs` : `URL · ${route.urls[0]}`;
    case 'text':  return 'Free text';
    case 'empty': return '';
  }
}
